import React, { useEffect, useState } from "react";
import { Modal, Button, Popconfirm, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import api from "../../services/Api";

/**
 * Tarjousilmoitus (Mika 26.9.2026): kun ilmoittajalla on avoin tarjous, se näytetään heti
 * sisäänkirjautumisen jälkeen ponnahdusikkunassa. Säännöt:
 *
 *  - Useasta avoimesta tarjouksesta näytetään VAIN suurin.
 *  - KESKEN OLEVASTA HUUTOKAUPASTA ei näytetä ikkunaa lainkaan ennen päättymistä.
 *  - Päättyneessä huutokaupassa, jossa on "Varmat kaupat" (confirmedSale), kauppa on jo
 *    sitova korkeimmalle tarjoajalle → ei hyväksy/hylkää-nappeja vaan "Luo kauppakirja".
 *  - Muissa tapauksissa Hyväksy / Hylkää (molemmissa varmistus).
 *
 * Ajastin: tarjous on voimassa 14 vrk (myynti-ilmoitus: tarjouksen jättämisestä, huutokauppa:
 * huutokaupan päättymisestä) — sama raja kuin AuctionService::getBidCancelAvailableAt, jonka
 * jälkeen ostaja voi perua tarjouksensa.
 *
 * Ikkuna ei saa koskaan rikkoa sivua: virheet niellään ja komponentti palauttaa null.
 * Sulkeminen ei kuittaa tarjousta — se palaa seuraavalla kirjautumisella.
 */
const VOIMASSA_VRK = 14;

/* Rajapinta palauttaa tarjouksen ajan muodossa d.m.Y H:i:s ja huutokaupan päättymisen
   PHP:n DateTime-rakenteena {date: "Y-m-d H:i:s.u", ...}. */
const parsiTarjousAika = (teksti) => {
    const osat = String(teksti || "").match(/^(\d{2})\.(\d{2})\.(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);

    if (!osat) return null;

    return new Date(
        Number(osat[3]),
        Number(osat[2]) - 1,
        Number(osat[1]),
        Number(osat[4]),
        Number(osat[5]),
        Number(osat[6] || 0)
    );
};

const parsiPaattyy = (arvo) => {
    const teksti = arvo && arvo.date ? arvo.date : arvo;

    if (!teksti) return null;

    const aika = new Date(String(teksti).replace(" ", "T").slice(0, 19));

    return Number.isNaN(aika.getTime()) ? null : aika;
};

const pvmTeksti = (pvm) =>
    `${pvm.getDate()}.${pvm.getMonth() + 1}.${pvm.getFullYear()} klo ${String(pvm.getHours()).padStart(2, "0")}.${String(
        pvm.getMinutes()
    ).padStart(2, "0")}`;

const jaljellaTeksti = (ms) => {
    const minuutit = Math.floor(ms / 60000);
    const paivat = Math.floor(minuutit / (60 * 24));
    const tunnit = Math.floor((minuutit % (60 * 24)) / 60);
    const min = minuutit % 60;

    if (paivat > 0) return `${paivat} pv ${tunnit} t`;
    if (tunnit > 0) return `${tunnit} t ${min} min`;

    return `${min} min`;
};

const TarjousIlmoitusModal = () => {
    const navigate = useNavigate();
    const [tarjous, setTarjous] = useState(null);
    const [kasittelee, setKasittelee] = useState(false);
    const [suljettu, setSuljettu] = useState(false);
    const [tulos, setTulos] = useState(null);
    const [nyt, setNyt] = useState(Date.now());
    /* Hyväksynnän jälkeen näytetään suoraan seuraava vaihe: kauppakirja. */
    const [hyvaksyttyUuid, setHyvaksyttyUuid] = useState(null);

    /* Kesken oleva huutokauppa jätetään kokonaan pois: siitä ei ilmoiteta ennen päättymistä. */
    const nayteettavat = (lista) =>
        (lista || []).filter((t) => {
            if (t?.auction?.type !== "auction") return true;

            const paattyy = parsiPaattyy(t?.auction?.ends);

            return !paattyy || paattyy.getTime() <= Date.now();
        });

    const suurinTarjous = (lista) =>
        nayteettavat(lista).reduce(
            (paras, t) => (paras === null || Number(t.amount) > Number(paras.amount) ? t : paras),
            null
        );

    const hae = async () => {
        if (!localStorage.getItem("token")) return;

        try {
            const data = await api.getAuctionOffers();
            setTarjous(suurinTarjous(data && data.pendingOffers));
        } catch (error) {
            setTarjous(null);
        }
    };

    useEffect(() => {
        hae();
    }, []);

    useEffect(() => {
        if (suljettu || !tarjous) return undefined;

        const ajastin = setInterval(() => setNyt(Date.now()), 60000);

        return () => clearInterval(ajastin);
    }, [suljettu, tarjous]);

    const kasittele = async (toiminto, viesti) => {
        if (!tarjous) return;
        setKasittelee(true);

        try {
            await toiminto(tarjous.id);
            setTulos(viesti);
            await hae();
        } catch (error) {
            setTulos("Toiminto ei onnistunut. Voitte hoitaa tarjouksen Oma sivu -osiossa.");
        } finally {
            setKasittelee(false);
        }
    };

    if (suljettu || (!tarjous && !tulos)) return null;

    const auction = tarjous ? tarjous.auction : null;
    const onHuutokauppa = auction?.type === "auction";
    const varmatKaupat = onHuutokauppa && !!auction?.confirmedSale;
    const paattyi = parsiPaattyy(auction?.ends);
    const annettu = parsiTarjousAika(tarjous?.createdAt);
    const laskennanAlku = onHuutokauppa ? paattyi || annettu : annettu;
    const maaraaika = laskennanAlku ? new Date(laskennanAlku.getTime() + VOIMASSA_VRK * 24 * 60 * 60 * 1000) : null;
    const jaljella = maaraaika ? maaraaika.getTime() - nyt : null;
    const kiire = jaljella !== null && jaljella < 24 * 60 * 60 * 1000;

    const otsikko = tulos
        ? "Kiitos"
        : varmatKaupat
        ? "Huutokauppanne on päättynyt — kauppa syntyi"
        : "Osakkeestanne on tehty tarjous";

    return (
        <Modal
            open
            centered
            maskClosable={false}
            onCancel={() => setSuljettu(true)}
            title={otsikko}
            footer={
                tulos ? (
                    <div className="flex flex-wrap gap-2 justify-end">
                        <Button onClick={() => setSuljettu(true)}>Sulje</Button>
                        {hyvaksyttyUuid && (
                            <Button
                                type="primary"
                                onClick={() => {
                                    setSuljettu(true);
                                    navigate(`/oma-sivu/sopimus/${hyvaksyttyUuid}`);
                                }}
                            >
                                Luo kauppakirja
                            </Button>
                        )}
                    </div>
                ) : varmatKaupat ? (
                    <div className="flex flex-wrap gap-2 justify-end">
                        <Button onClick={() => setSuljettu(true)}>Katson myöhemmin</Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                setSuljettu(true);
                                navigate(`/oma-sivu/sopimus/${auction?.uuid}`);
                            }}
                        >
                            Luo kauppakirja
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-2 justify-end">
                        <Button onClick={() => setSuljettu(true)}>Katson myöhemmin</Button>
                        <Popconfirm
                            title="Hylätäänkö tarjous?"
                            okText="Kyllä, hylkää"
                            cancelText="Peruuta"
                            onConfirm={() => kasittele(api.rejectOffer, "Tarjous on hylätty.")}
                        >
                            <Button danger>Hylkää</Button>
                        </Popconfirm>
                        <Popconfirm
                            title="Hyväksytäänkö tarjous? Tämän jälkeen voitte tehdä kauppakirjan."
                            okText="Kyllä, hyväksy"
                            cancelText="Peruuta"
                            onConfirm={() => {
                                setHyvaksyttyUuid(auction?.uuid || null);
                                kasittele(
                                    api.acceptOffer,
                                    "Tarjous on hyväksytty. Seuraava vaihe on kauppakirja."
                                );
                            }}
                        >
                            <Button type="primary">Hyväksy</Button>
                        </Popconfirm>
                    </div>
                )
            }
        >
            {kasittelee ? (
                <div className="py-6 text-center">
                    <Spin />
                </div>
            ) : tulos ? (
                <p className="text-base">{tulos}</p>
            ) : (
                <div className="space-y-3 text-base">
                    <div>
                        <div className="text-sm text-gray-500">Kohde</div>
                        <div className="font-bold">
                            #{auction?.number} {auction?.title}
                        </div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-500">
                            {varmatKaupat ? "Korkein tarjous" : "Tarjous"}
                        </div>
                        <div className="font-bold text-xl">{tarjous.amount} €</div>
                    </div>
                    {tarjous.createdAt && (
                        <div>
                            <div className="text-sm text-gray-500">Tarjous annettu</div>
                            <div>{tarjous.createdAt}</div>
                        </div>
                    )}
                    {varmatKaupat ? (
                        <p className="text-base">
                            Huutokauppa oli varmat kaupat, eli sitouduitte myymään kohteen korkeimman tarjouksen
                            tehneelle. Kauppa on siis syntynyt — seuraavaksi tehdään kauppakirja, jonka molemmat
                            osapuolet allekirjoittavat sähköisesti.
                        </p>
                    ) : (
                        <p className="text-sm text-gray-600">
                            Tarjouksen hyväksyminen tai hylkääminen on teidän päätöksenne. Kauppakirjan voi tehdä
                            vasta kun tarjous on hyväksytty — sen jälkeen se laaditaan ja allekirjoitetaan
                            palvelussa sähköisesti.
                        </p>
                    )}
                    {maaraaika && !varmatKaupat && (
                        <div
                            className={`rounded p-3 ${kiire ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-800"}`}
                        >
                            <div className="text-sm">Aikaa vastata</div>
                            {jaljella > 0 ? (
                                <>
                                    <div className="font-bold text-lg">{jaljellaTeksti(jaljella)}</div>
                                    <div className="text-sm">
                                        Tarjous on voimassa {pvmTeksti(maaraaika)} asti. Sen jälkeen ostaja voi perua
                                        tarjouksensa.
                                    </div>
                                </>
                            ) : (
                                <div className="font-bold">
                                    Vastausaika umpeutui {pvmTeksti(maaraaika)} — ostaja voi perua tarjouksen milloin
                                    tahansa. Käsitelkää se pian.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default TarjousIlmoitusModal;
